import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Login.css';
import t from '../../i18n/useTranslation';
import { getPublicChannel } from '../../services/api';
import { COUNTRIES_LIST, getRegionsForCountry } from '../../constants/countries';
import EmailVerifyModal from '../EmailVerifyModal/EmailVerifyModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

// Idiomas soportados — mismos que ProfileModal
const LANGUAGES = [
    'español', 'inglés', 'francés', 'alemán', 'portugués', 'italiano',
    'euskera', 'chino', 'japonés', 'árabe', 'hindi', 'ruso', 'coreano',
    'turco', 'indonesio'
];

function Login({ onLogin }) {
    const [mode, setMode] = useState('login');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [regName, setRegName] = useState('');
    const [regPhone, setRegPhone] = useState('');
    const [regEmail, setRegEmail] = useState(''); // 2026-05-06: email para verify
    const [regPassword, setRegPassword] = useState('');
    const [regPassword2, setRegPassword2] = useState('');

    // Estado del modal de verificación post-registro (Turnstile + Email OTP)
    const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
    // Antibot: honeypot (oculto, rellenado solo por bots) + marca de apertura
    // del formulario para que registros completados en <1.5s sean rechazados.
    const [regHoneypot, setRegHoneypot] = useState('');
    const regOpenedAtRef = useRef(Date.now());
    const [showRegPassword, setShowRegPassword] = useState(false);
    const [showRegPassword2, setShowRegPassword2] = useState(false);
    const [regLanguage, setRegLanguage] = useState('español');
    const [regCountry, setRegCountry] = useState('');
    const [regRegion, setRegRegion] = useState('');
    const [pendingChannel, setPendingChannel] = useState(null);

    // Regiones disponibles para el país seleccionado (cambia al cambiar país)
    const availableRegions = useMemo(
        () => (regCountry ? getRegionsForCountry(regCountry) : []),
        [regCountry]
    );

    useEffect(() => {
        const code = localStorage.getItem('kaixo_pending_channel');
        if (!code) return;
        const lang = (navigator.language || 'en').startsWith('es') ? 'español' : 'inglés';
        getPublicChannel(code, lang)
            .then(({ data }) => setPendingChannel(data))
            .catch(() => setPendingChannel(null));
    }, []);

    // Validador de formato E.164: "+" obligatorio, 1-3 dígitos código país,
    // luego 6-15 dígitos. Permite espacios entre grupos pero los limpia antes
    // de comparar y de enviar al backend para que el matching de contactos
    // funcione siempre con dígitos puros.
    const cleanPhone = (raw) => String(raw || '').replace(/\s+/g, '');
    const isValidPhone = (raw) => /^\+\d{8,16}$/.test(cleanPhone(raw));
    const phoneFormatHint = t.login?.phoneFormatHint
        || 'Escribe el teléfono con código país. Ejemplo: +34634123456';

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!phone || !password) { setError(t.login.error.phoneRequired); return; }
        if (!isValidPhone(phone)) { setError(phoneFormatHint); return; }
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_URL}/users/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: cleanPhone(phone), password })
            });
            const data = await response.json();
            if (data.success) {
                if (data.token) localStorage.setItem('kaixo_token', data.token);
                localStorage.setItem('kaixo_user', JSON.stringify(data.user));
                onLogin(data.user);
            } else {
                setError(data.error || t.login.error.phonePassword);
            }
        } catch (err) {
            setError(t.login.error.connection);
        }
        setLoading(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!regName || !regPhone || !regPassword || !regLanguage) { setError(t.login.error.fillRequired); return; }
        if (!isValidPhone(regPhone)) { setError(phoneFormatHint); return; }
        if (regPassword !== regPassword2) { setError(t.login.error.passwordMismatch); return; }
        if (regPassword.length < 6) { setError(t.login.error.passwordLength); return; }
        // Email opcional pero si se introduce, validar formato
        if (regEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail)) {
            setError('Email inválido');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_URL}/users/test-create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: regName,
                    phone: cleanPhone(regPhone),
                    email: regEmail || undefined,
                    password: regPassword,
                    language: regLanguage,
                    country: regCountry,
                    region: regRegion,
                    // Antibot: el honeypot debe viajar vacío (humano); los
                    // bots que autocompletan lo rellenan y el backend los
                    // rechaza. form_opened_at permite filtrar envíos <1.5s.
                    website_confirm: regHoneypot,
                    form_opened_at: regOpenedAtRef.current,
                })
            });
            const data = await response.json();
            if (data.success) {
                if (data.token) localStorage.setItem('kaixo_token', data.token);
                localStorage.setItem('kaixo_user', JSON.stringify(data.user));

                // Si el user dio email, abrir EmailVerifyModal (Turnstile + OTP).
                // Si no dio email, login directo — podrá verificar desde Settings después.
                if (regEmail) {
                    setPendingVerifyEmail(regEmail);
                    setLoading(false);
                    return; // NO llamar onLogin aún — el modal lo hará al verificar
                }
                onLogin(data.user);
            } else {
                setError(data.error || t.login.error.registerFailed);
            }
        } catch (err) {
            // Log detallado en consola para diagnosticar en WebView Android
            console.error('[Register] error de red:', err?.message, err);
            setError(`${t.login.error.connection}: ${err?.message || 'desconocido'}`);
        }
        setLoading(false);
    };

    return (
        <div className="login-container">
            <div className="login-bg" />
            <div className="login-card">
                <div className="login-logo-wrap">
                    <img src={require('../../assets/favicon.png')} alt="Kaixo" className="login-logo" />
                </div>
                <h1 className="login-title">KAIXO</h1>
                <p className="login-subtitle">{t.app.subtitle}</p>

                {pendingChannel?.channel?.name && (
                    <div className="pending-channel-banner">
                        <div className="pcb-icon">📢</div>
                        <div className="pcb-content">
                            <div className="pcb-title">{pendingChannel.channel.name}</div>
                            {pendingChannel.channel.description && (
                                <div className="pcb-desc">{pendingChannel.channel.description}</div>
                            )}
                            {Array.isArray(pendingChannel.messages) && pendingChannel.messages.length > 0 && (
                                <div className="pcb-preview">
                                    {(pendingChannel.messages[pendingChannel.messages.length - 1]?.text || '').slice(0, 100)}
                                </div>
                            )}
                            <div className="pcb-cta">{t.login?.pendingChannelCta || 'Sign in to subscribe automatically'}</div>
                        </div>
                    </div>
                )}

                <div className="login-tabs">
                    <button className={`login-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); }}>
                        {t.login.signin}
                    </button>
                    <button className={`login-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => { setMode('register'); setError(''); }}>
                        {t.login.signup}
                    </button>
                </div>


                {mode === 'login' ? (
                    <form className="login-form" onSubmit={handleLogin}>
                        <div className="login-field">
                            <label>{t.login.phone}</label>
                            <input
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder={t.login.phonePlaceholder}
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                            />
                            <small className="login-hint">{phoneFormatHint}</small>
                        </div>
                        <div className="login-field">
                            <label>{t.login.password}</label>
                            <div className="password-wrap">
                                <input type={showPassword ? 'text' : 'password'} placeholder={t.login.passwordPlaceholder} value={password} onChange={e => setPassword(e.target.value)} />
                                <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>
                        {error && <div className="login-error">{error}</div>}
                        <button type="submit" className="login-btn" disabled={loading}>
                            {loading ? t.login.entering : t.login.enter}
                        </button>
                    </form>
                ) : (
                    <form className="login-form" onSubmit={handleRegister}>
                        {/* Honeypot: invisible para humanos (off-screen, tabindex -1,
                            aria-hidden). Los bots que autocompletan por name/autocomplete
                            lo rellenan y el backend los rechaza. */}
                        <input
                            type="text"
                            name="website_confirm"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            value={regHoneypot}
                            onChange={e => setRegHoneypot(e.target.value)}
                            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
                        />
                        <div className="login-field">
                            <label>{t.login.name} *</label>
                            <input type="text" placeholder={t.login.namePlaceholder} value={regName} onChange={e => setRegName(e.target.value)} maxLength={50} />
                        </div>
                        <div className="login-field">
                            <label>{t.login.phone} *</label>
                            <input
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder={t.login.phonePlaceholder}
                                value={regPhone}
                                onChange={e => setRegPhone(e.target.value)}
                            />
                            <small className="login-hint">{phoneFormatHint}</small>
                        </div>
                        {/* Email opcional para verificación + reset password.
                            Si lo introduce, tras crear cuenta abrimos EmailVerifyModal
                            (Turnstile + OTP). Si no, login directo y queda
                            emailVerified=false hasta que lo añada en Settings. */}
                        <div className="login-field">
                            <label>{t.login.email || 'Correo (opcional)'}</label>
                            <input
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                placeholder="tu@email.com"
                                value={regEmail}
                                onChange={e => setRegEmail(e.target.value)}
                            />
                            <small className="login-hint" style={{ color: '#888', fontSize: 11 }}>
                                Cero anuncios. Solo para verificación y reset de contraseña.
                            </small>
                        </div>
                        <div className="login-field">
                            <label>{t.login.password} *</label>
                            <div className="password-wrap">
                                <input type={showRegPassword ? 'text' : 'password'} placeholder={t.login.passwordMin} value={regPassword} onChange={e => setRegPassword(e.target.value)} />
                                <button type="button" className="eye-btn" onClick={() => setShowRegPassword(!showRegPassword)}>
                                    {showRegPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>
                        <div className="login-field">
                            <label>{t.login.repeatPassword} *</label>
                            <div className="password-wrap">
                                <input type={showRegPassword2 ? 'text' : 'password'} placeholder={t.login.repeatPasswordPlaceholder} value={regPassword2} onChange={e => setRegPassword2(e.target.value)} />
                                <button type="button" className="eye-btn" onClick={() => setShowRegPassword2(!showRegPassword2)}>
                                    {showRegPassword2 ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>
                        <div className="login-field">
                            <label>{t.login.language} *</label>
                            <select
                                value={regLanguage}
                                onChange={e => setRegLanguage(e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 14, background: '#FFF' }}
                            >
                                {LANGUAGES.map(lang => (
                                    <option key={lang} value={lang}>{lang}</option>
                                ))}
                            </select>
                        </div>
                        <div className="login-field">
                            <label>País</label>
                            <select
                                value={regCountry}
                                onChange={e => { setRegCountry(e.target.value); setRegRegion(''); }}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 14, background: '#FFF' }}
                            >
                                <option value="">— Selecciona país —</option>
                                {COUNTRIES_LIST.map(c => (
                                    <option key={c.id} value={c.id}>{c.native}</option>
                                ))}
                            </select>
                        </div>
                        {regCountry && availableRegions.length > 0 && (
                            <div className="login-field">
                                <label>Región / Estado</label>
                                <select
                                    value={regRegion}
                                    onChange={e => setRegRegion(e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 14, background: '#FFF' }}
                                >
                                    <option value="">— Opcional —</option>
                                    {availableRegions.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {error && <div className="login-error">{error}</div>}
                        <button type="submit" className="login-btn" disabled={loading}>
                            {loading ? t.login.creating : t.login.createAccount}
                        </button>
                    </form>
                )}
            </div>

            {/* EmailVerifyModal — se abre tras crear cuenta SI el user dio email.
                Maneja Turnstile (Cloudflare) + código OTP de 6 dígitos vía Resend.
                Si verifica con éxito, hace login completo. Si cancela, queda
                con cuenta creada pero emailVerified=false (puede verificar luego). */}
            {pendingVerifyEmail && (
                <EmailVerifyModal
                    email={pendingVerifyEmail}
                    language={regLanguage}
                    onVerified={(user) => {
                        setPendingVerifyEmail(null);
                        // Mergear emailVerified actualizado con el user en localStorage
                        try {
                            const stored = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
                            const merged = { ...stored, ...(user || {}), emailVerified: true };
                            localStorage.setItem('kaixo_user', JSON.stringify(merged));
                            onLogin(merged);
                        } catch {
                            onLogin(user);
                        }
                    }}
                    onCancel={() => {
                        // User cancela verificación — login con la cuenta sin verificar
                        // (puede completar verificación más tarde desde Settings).
                        setPendingVerifyEmail(null);
                        try {
                            const stored = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
                            onLogin(stored);
                        } catch {
                            onLogin({ emailVerified: false });
                        }
                    }}
                />
            )}
        </div>
    );
}

export default Login;